import { useState, useEffect, useCallback, Fragment } from 'react'
import { supabase } from '../lib/supabase'
import { buildCostControlDataFromRelease } from '../lib/buildCostControlData'
import { buildReleaseComparison } from '../lib/buildReleaseComparison'
import { exportComparisonPdf } from '../lib/exportComparisonPdf'

// ─────────────────────────────────────────────────────────────────────────
// ReleaseComparisonView — the "Compare Releases" tab content inside
// ReleaseManagement.jsx.
//
// REDESIGNED (per explicit feedback): always ONE merged tree — Direct
// Works on top, Indirect Works below, exactly the same hierarchy/columns
// shape as Cost Control/Forecast (Cost Code -> Resource -> Sub Code) — no
// separate/alternate screen design, no Direct/Indirect toggle. Only three
// value columns: Version 1 (base), Version 2 (compare), Variance. No
// Budget column — this view is EAC-vs-EAC only.
//
// A "Comparing" bar makes explicit which Direct/Indirect Revision is being
// compared against which, and flags when a side's Revision is identical in
// both Releases (so a wall of zero-variance Indirect rows isn't confusing —
// the user is told up front why).
//
// Owns everything Variance Notes (loading, editing, saving) —
// buildReleaseComparison() itself has zero knowledge of notes.
// Generate Comparison stays an explicit button — changing a dropdown/mode/
// scope control never silently re-runs the comparison.
export default function ReleaseComparisonView({ projectId }) {
  const [releases, setReleases] = useState([])
  const [loadingReleases, setLoadingReleases] = useState(true)
  const [baseReleaseId, setBaseReleaseId] = useState(null)
  const [compareReleaseId, setCompareReleaseId] = useState(null)
  const [mode, setMode] = useState('full')
  const [scopeType, setScopeType] = useState('project')
  const [scopeCostCodeCode, setScopeCostCodeCode] = useState('')
  const [availableCostCodes, setAvailableCostCodes] = useState([])

  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState(null)
  const [comparison, setComparison] = useState(null)
  const [comparisonMeta, setComparisonMeta] = useState(null)
  const [expandedCodes, setExpandedCodes] = useState(new Set())

  const [notes, setNotes] = useState({})
  const [savingNoteKey, setSavingNoteKey] = useState(null)

  const loadReleases = useCallback(async () => {
    if (!projectId) return
    setLoadingReleases(true)
    const { data } = await supabase
      .from('project_forecast_release_summary')
      .select('release_id, project_id, forecast_date, release_number, direct_snapshot_id, direct_revision_number, indirect_snapshot_id, indirect_revision_number, notes, created_at, is_current')
      .eq('project_id', projectId)
      .order('forecast_date', { ascending: false })
      .order('release_number', { ascending: false })
    setReleases(data || [])
    setLoadingReleases(false)
  }, [projectId])

  useEffect(() => { loadReleases() }, [loadReleases])

  useEffect(() => {
    if (!projectId) return
    supabase.from('cost_codes').select('code, description').eq('project_id', projectId).is('parent_id', null).order('code')
      .then(({ data }) => setAvailableCostCodes(data || []))
  }, [projectId])

  async function loadNotesFor(baseId, compareId) {
    const { data } = await supabase
      .from('release_comparison_notes')
      .select('id, node_type, node_code, note')
      .eq('base_release_id', baseId)
      .eq('compare_release_id', compareId)
    const map = {}
    for (const row of (data || [])) {
      map[`${row.node_type}:${row.node_code}`] = { id: row.id, note: row.note }
    }
    setNotes(map)
  }

  async function saveNote(nodeType, nodeCode, text) {
    if (!baseReleaseId || !compareReleaseId) return
    const key = `${nodeType}:${nodeCode}`
    setSavingNoteKey(key)
    try {
      const { data, error: upsertError } = await supabase
        .from('release_comparison_notes')
        .upsert({
          project_id: projectId,
          base_release_id: baseReleaseId,
          compare_release_id: compareReleaseId,
          node_type: nodeType,
          node_code: nodeCode,
          note: text,
        }, { onConflict: 'base_release_id,compare_release_id,node_type,node_code' })
        .select('id, note')
        .single()
      if (upsertError) throw upsertError
      setNotes(prev => ({ ...prev, [key]: { id: data.id, note: data.note } }))
    } catch (e) {
      console.error('Failed to save note:', e)
    } finally {
      setSavingNoteKey(null)
    }
  }

  async function handleGenerate() {
    if (!baseReleaseId || !compareReleaseId) {
      setError('Select both Version 1 (Base) and Version 2 (Compare) releases.')
      return
    }
    if (baseReleaseId === compareReleaseId) {
      setError('Version 1 and Version 2 must be two different releases.')
      return
    }
    setError(null)
    setGenerating(true)
    try {
      const baseRelease = releases.find(r => r.release_id === baseReleaseId)
      const compareRelease = releases.find(r => r.release_id === compareReleaseId)

      const [baseReportData, compareReportData] = await Promise.all([
        buildCostControlDataFromRelease(baseRelease),
        buildCostControlDataFromRelease(compareRelease),
      ])

      const scope = scopeType === 'costcode' && scopeCostCodeCode
        ? { costCodeCode: scopeCostCodeCode }
        : 'project'

      const result = buildReleaseComparison(baseReportData, compareReportData, { mode, scope, baseRelease, compareRelease })
      setComparison(result)
      setComparisonMeta({ base: baseRelease, compare: compareRelease })
      setExpandedCodes(new Set([...result.directDiffs.map(d => d.code), ...result.indirectDiffs.map(d => d.code)]))
      await loadNotesFor(baseReleaseId, compareReleaseId)
    } catch (e) {
      console.error(e)
      setError(e.message || String(e))
      setComparison(null)
      setComparisonMeta(null)
    } finally {
      setGenerating(false)
    }
  }

  function toggleExpand(code) {
    setExpandedCodes(prev => {
      const next = new Set(prev)
      if (next.has(code)) next.delete(code); else next.add(code)
      return next
    })
  }

  function handleExportPdf() {
    if (!comparison || !comparisonMeta) return
    exportComparisonPdf(comparison, {
      mode, scope: scopeType === 'costcode' ? { costCodeCode: scopeCostCodeCode } : 'project',
      baseRelease: comparisonMeta.base,
      compareRelease: comparisonMeta.compare,
      notes,
      projectId,
    })
  }

  const releaseLabel = (r) => r
    ? `#${r.release_number} — ${new Date(r.forecast_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`
    : '—'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#F9FAFB' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '16px 24px', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Version 1 (Base Release)</label>
            <select value={baseReleaseId || ''} onChange={e => setBaseReleaseId(e.target.value || null)}
              disabled={loadingReleases} style={selectStyle}>
              <option value="">Select…</option>
              {releases.map(r => <option key={r.release_id} value={r.release_id}>{releaseLabel(r)}</option>)}
            </select>
          </div>

          <div style={{ fontSize: 16, color: '#9CA3AF', paddingBottom: 8 }}>vs</div>

          <div>
            <label style={labelStyle}>Version 2 (Compare Release)</label>
            <select value={compareReleaseId || ''} onChange={e => setCompareReleaseId(e.target.value || null)}
              disabled={loadingReleases} style={selectStyle}>
              <option value="">Select…</option>
              {releases.map(r => <option key={r.release_id} value={r.release_id}>{releaseLabel(r)}</option>)}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Show</label>
            <div style={{ display: 'flex', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
              <button onClick={() => setMode('full')} style={toggleBtnStyle(mode === 'full')}>Full Tree</button>
              <button onClick={() => setMode('changed')} style={{ ...toggleBtnStyle(mode === 'changed'), borderLeft: '1px solid #E5E7EB' }}>Changed Only</button>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Scope</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <select value={scopeType} onChange={e => setScopeType(e.target.value)} style={selectStyle}>
                <option value="project">Entire Project</option>
                <option value="costcode">Selected Cost Code</option>
              </select>
              {scopeType === 'costcode' && (
                <select value={scopeCostCodeCode} onChange={e => setScopeCostCodeCode(e.target.value)} style={{ ...selectStyle, minWidth: 220 }}>
                  <option value="">Select Cost Code…</option>
                  {availableCostCodes.map(c => <option key={c.code} value={c.code}>{c.code} — {c.description}</option>)}
                </select>
              )}
            </div>
          </div>

          <button onClick={handleGenerate} disabled={generating}
            style={{ padding: '8px 18px', background: '#1A7A4A', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: generating ? 0.6 : 1 }}>
            {generating ? 'Generating…' : 'Generate Comparison'}
          </button>

          {comparison && (
            <button onClick={handleExportPdf}
              style={{ padding: '8px 16px', background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
              ↓ PDF
            </button>
          )}
        </div>
        {error && <p style={{ fontSize: 12, color: '#DC2626', marginTop: 10 }}>{error}</p>}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        {!comparison ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 8 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>Select Version 1 and Version 2, then press Generate Comparison.</p>
          </div>
        ) : (
          <>
            <ComparingBar meta={comparisonMeta} revisionMeta={comparison.revisionMeta} />
            <SummaryBar summary={comparison.summary} />
            <MergedComparisonTree
              directDiffs={comparison.directDiffs}
              indirectDiffs={comparison.indirectDiffs}
              expandedCodes={expandedCodes} onToggle={toggleExpand}
              notes={notes} onSaveNote={saveNote} savingNoteKey={savingNoteKey}
            />
          </>
        )}
      </div>
    </div>
  )
}

const labelStyle = { display: 'block', fontSize: 10.5, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }
const selectStyle = { padding: '7px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, minWidth: 200 }
function toggleBtnStyle(active) {
  return { padding: '7px 12px', border: 'none', background: active ? '#1A7A4A' : '#fff', color: active ? '#fff' : '#374151', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }
}

function ComparingBar({ meta, revisionMeta }) {
  if (!meta) return null
  const row = (label, rev) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: 10, letterSpacing: '.05em', color: '#065F46', minWidth: 64 }}>{label}</span>
      <span style={{ fontSize: 12.5 }}>{rev.baseRevisionLabel} <span style={{ color: '#9CA3AF' }}>{'->'}</span> {rev.compareRevisionLabel}</span>
      {rev.sameRevision && (
        <span style={{ fontSize: 10.5, fontWeight: 700, color: '#B7770D', background: '#FEF3C7', padding: '2px 8px', borderRadius: 999 }}>
          Same revision — no change expected
        </span>
      )}
    </div>
  )
  return (
    <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: '#065F46', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>Comparing</div>
      {row('Direct', revisionMeta.direct)}
      {row('Indirect', revisionMeta.indirect)}
    </div>
  )
}

function SummaryBar({ summary }) {
  const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)
  const cards = [
    ['Total Variance', fmt(summary.totalVariance), summary.totalVariance >= 0 ? '#1A7A4A' : '#DC2626'],
    ['Variance %', (summary.totalVariancePct || 0).toFixed(1) + '%', summary.totalVariance >= 0 ? '#1A7A4A' : '#DC2626'],
    ['Changed Items', summary.changedCount, '#D97706'],
    ['New Items', summary.newCount, '#1D6FA4'],
    ['Removed Items', summary.removedCount, '#DC2626'],
  ]
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        {cards.map(([label, value, color]) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 16px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>
      {summary.topVariances.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Top 10 Variances</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {summary.topVariances.map(n => (
              <div key={`${n.level}:${n.code}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, padding: '5px 4px', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em',
                  color: n.level === 'costcode' ? '#0F1017' : n.level === 'resource' ? '#7C3AED' : '#6B7280',
                  background: n.level === 'costcode' ? '#F3F4F6' : n.level === 'resource' ? '#F3EEFC' : '#F9FAFB',
                  padding: '2px 7px', borderRadius: 4, minWidth: 62, textAlign: 'center', flexShrink: 0,
                }}>
                  {n.level === 'costcode' ? 'Cost Code' : n.level === 'resource' ? 'Resource' : 'Sub Code'}
                </span>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#374151', flexShrink: 0 }}>{n.code}</span>
                <span style={{ color: '#6B7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.description}</span>
                <span style={{ fontWeight: 700, color: n.variance >= 0 ? '#1A7A4A' : '#DC2626', flexShrink: 0 }}>
                  {n.variance >= 0 ? '+' : ''}{fmt(n.variance)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function statusColor(status) {
  return status === 'new' ? '#1D6FA4' : status === 'removed' ? '#DC2626' : status === 'changed' ? '#D97706' : '#D1D5DB'
}

function MergedComparisonTree({ directDiffs, indirectDiffs, expandedCodes, onToggle, notes, onSaveNote, savingNoteKey }) {
  const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n || 0)

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, background: '#fff', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
            <th style={thStyle('left')}>Code · Description</th>
            <th style={thStyle('right')}>Version 1</th>
            <th style={thStyle('right')}>Version 2</th>
            <th style={thStyle('right')}>Variance</th>
            <th style={thStyle('left')}>Note</th>
          </tr>
        </thead>
        <tbody>
          {directDiffs.length > 0 && <SectionHeaderRow title="Direct Works" />}
          {directDiffs.map(cc => (
            <CostCodeRows key={cc.code} node={cc} expandedCodes={expandedCodes} onToggle={onToggle}
              notes={notes} onSaveNote={onSaveNote} savingNoteKey={savingNoteKey} fmt={fmt} />
          ))}
          {indirectDiffs.length > 0 && <SectionHeaderRow title="Indirect Works" />}
          {indirectDiffs.map(cc => (
            <CostCodeRows key={cc.code} node={cc} expandedCodes={expandedCodes} onToggle={onToggle}
              notes={notes} onSaveNote={onSaveNote} savingNoteKey={savingNoteKey} fmt={fmt} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function thStyle(align) {
  return { padding: '8px 12px', textAlign: align, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#6B7280' }
}

function SectionHeaderRow({ title }) {
  return (
    <tr>
      <td colSpan={5} style={{ padding: '8px 12px', background: '#0F1017', color: '#fff', fontWeight: 800, fontSize: 12 }}>{title}</td>
    </tr>
  )
}

function CostCodeRows({ node, expandedCodes, onToggle, notes, onSaveNote, savingNoteKey, fmt }) {
  const expanded = expandedCodes.has(node.code)
  const orphanChildren = (node.children || []).filter(c => !c.parent_id)
  const childrenByParent = {}
  for (const c of (node.children || [])) {
    if (c.parent_id) (childrenByParent[c.parent_id] = childrenByParent[c.parent_id] || []).push(c)
  }

  return (
    <>
      <DiffRow node={node} level={0} onToggle={() => onToggle(node.code)} expanded={expanded}
        hasChildren={(node.resourceRows?.length > 0) || (node.children?.length > 0)}
        notes={notes} onSaveNote={onSaveNote} savingNoteKey={savingNoteKey} fmt={fmt} nodeType="cost_code" />
      {expanded && (node.resourceRows || []).map(res => (
        <Fragment key={res.code}>
          <DiffRow node={res} level={1}
            notes={notes} onSaveNote={onSaveNote} savingNoteKey={savingNoteKey} fmt={fmt} nodeType="resource" />
          {(childrenByParent[res.code] || []).map(sub => (
            <DiffRow key={sub.code} node={sub} level={2}
              notes={notes} onSaveNote={onSaveNote} savingNoteKey={savingNoteKey} fmt={fmt} nodeType="sub_code" />
          ))}
        </Fragment>
      ))}
      {expanded && orphanChildren.map(sub => (
        <DiffRow key={sub.code} node={sub} level={1}
          notes={notes} onSaveNote={onSaveNote} savingNoteKey={savingNoteKey} fmt={fmt} nodeType="sub_code" />
      ))}
    </>
  )
}

function levelRowStyle(level, status) {
  const borderColor = status !== 'unchanged' ? statusColor(status) : 'transparent'
  if (level === 0) return { background: '#F9FAFB', borderLeft: `3px solid ${borderColor}` }
  if (level === 1) return { background: '#F3EEFC', borderLeft: `3px solid ${borderColor}` }
  return { background: '#fff', borderLeft: `3px solid ${borderColor}` }
}
function levelCodeStyle(level) {
  if (level === 0) return { fontWeight: 800, color: '#0F1017' }
  if (level === 1) return { fontWeight: 700, color: '#7C3AED' }
  return { fontWeight: 500, color: '#374151' }
}

function DiffRow({ node, level, onToggle, expanded, hasChildren, notes, onSaveNote, savingNoteKey, fmt, nodeType }) {
  const key = `${nodeType}:${node.code}`
  const existingNote = notes[key]?.note || ''
  const [draft, setDraft] = useState(existingNote)
  const isSaving = savingNoteKey === key
  const rowStyle = levelRowStyle(level, node.status)
  const codeStyle = levelCodeStyle(level)

  useEffect(() => { setDraft(existingNote) }, [existingNote])

  return (
    <tr style={{ borderBottom: '1px solid #F3F4F6', background: rowStyle.background }}>
      <td style={{ padding: '7px 12px', paddingLeft: 12 + level * 22, borderLeft: rowStyle.borderLeft }}>
        {hasChildren ? (
          <button onClick={onToggle} style={{ border: 'none', background: 'none', cursor: 'pointer', marginRight: 6, color: '#6B7280', fontSize: 11 }}>
            {expanded ? '▾' : '▸'}
          </button>
        ) : (
          <span style={{ display: 'inline-block', width: 16 }} />
        )}
        <span style={{ fontFamily: 'monospace', ...codeStyle }}>{node.code}</span>
        <span style={{ color: '#6B7280', marginLeft: 8 }}>{node.description}</span>
      </td>
      <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(node.baseValue)}</td>
      <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmt(node.compareValue)}</td>
      <td style={{ padding: '7px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: node.variance >= 0 ? '#1A7A4A' : '#DC2626' }}>
        {node.variance !== 0 ? (node.variance >= 0 ? '+' : '') + fmt(node.variance) : '—'}
      </td>
      <td style={{ padding: '7px 12px', minWidth: 220 }}>
        <input value={draft} onChange={e => setDraft(e.target.value)}
          onBlur={() => { if (draft !== existingNote) onSaveNote(nodeType, node.code, draft) }}
          placeholder="Add note…" disabled={isSaving}
          style={{ width: '100%', padding: '4px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11.5, opacity: isSaving ? 0.6 : 1 }} />
      </td>
    </tr>
  )
}
